import React, { useEffect, useMemo, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { Typography, makeStyles } from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useIntl } from 'react-intl'

import { studentCourseActions } from '../../../_state/actions'
import { CapeeshColors } from '../../../assets/ColorPalette'
import StudentCourseGroupCard from './StudentCourseGroupCard'
import CourseDetailPanel from '../CourseMenu/CourseDetailPanel'
import { studentScreenMessages } from '../../../Views/StudentPrivate/StudentScreen.i18n.Messages'

import type {
    CourseGroupMetadataV2Dto,
    CourseGroupV1Dto,
    CourseGroupProgressionV1Dto,
    StudentCourseState,
} from '../../../Logic/StudentCourseDtos'

// ─── Styles ───────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column' as const,
        minHeight: '100%',
        padding: '32px 24px',
        backgroundColor: CapeeshColors.EggShell2,
        '@media (max-width: 599px)': {
            padding: '20px 16px',
        },
    },
    header: {
        marginBottom: 28,
        '@media (max-width: 599px)': {
            marginBottom: 20,
        },
    },
    title: {
        fontFamily: 'Rubik',
        fontSize: 28,
        fontWeight: 600,
        color: CapeeshColors.Primary900,
        lineHeight: '130%',
        '@media (max-width: 599px)': {
            fontSize: 22,
        },
    },
    subtitle: {
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 400,
        color: CapeeshColors.Gray1,
        marginTop: 4,
    },
    cardGrid: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 20,
        '@media (max-width: 599px)': {
            flexDirection: 'column' as const,
            gap: 16,
        },
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 300,
        gap: 16,
    },
    loadingText: {
        fontFamily: 'Rubik',
        fontSize: 14,
        color: CapeeshColors.Gray1,
    },
    emptyContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 300,
        gap: 12,
    },
    emptyTitle: {
        fontFamily: 'Rubik',
        fontSize: 20,
        fontWeight: 500,
        color: CapeeshColors.DarkText,
    },
    emptySubtitle: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 400,
        color: CapeeshColors.Gray1,
        textAlign: 'center' as const,
        maxWidth: 360,
    },
    placeholderCard: {
        width: 264,
        height: 340,
        borderRadius: 16,
        backgroundColor: CapeeshColors.Gray5,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '@media (max-width: 599px)': {
            width: '100%',
            height: 200,
        },
    },
}))

// ─── Types ────────────────────────────────────────────────────────

interface MainMenuOwnProps {
    // Props passed by parent (currently none, but extensible)
}

interface MainMenuStateProps {
    studentCourse: StudentCourseState
}

interface MainMenuDispatchProps {
    dispatch: (action: any) => void
}

type MainMenuProps = MainMenuOwnProps & MainMenuStateProps & MainMenuDispatchProps

interface OrderedCourseGroupEntry {
    metadata: CourseGroupMetadataV2Dto
    details: CourseGroupV1Dto | null
    normalizedProgression: number | null
}

// ─── Component ────────────────────────────────────────────────────

const MainMenu: React.FC<MainMenuProps> = ({ dispatch, studentCourse }) => {
    const classes = useStyles()
    const intl = useIntl()
    const rootRef = useRef<HTMLDivElement>(null)
    const savedScrollRef = useRef<number>(0)

    const {
        courseGroupOverview,
        fetchingCourseGroupOverview,
        courseGroupDetailsMap,
        fetchingCourseGroupDetailsMap,
        courseGroupProgressionMap,
        fetchingCourseGroupProgressionMap,
        courseLessonOrder,
        courseLessonOrderProgression,
        fetchingCourseLessonOrder,
        fetchingCourseLessonOrderProgression,
    } = studentCourse

    // ─── Step 1: Fetch course group overview on mount ─────────────
    useEffect(() => {
        dispatch(studentCourseActions.getCourseGroupOverview())
    }, [])

    // ─── Step 2: When overview loads, fetch each course group's full details
    useEffect(() => {
        if (!courseGroupOverview?.courseGroupMetadataV2Dtos || !Array.isArray(courseGroupOverview.courseGroupMetadataV2Dtos)) {
            return
        }

        courseGroupOverview.courseGroupMetadataV2Dtos.forEach((metadata: CourseGroupMetadataV2Dto) => {
            if (!metadata?.courseGroupId) {
                return
            }

            const courseGroupId: string = metadata.courseGroupId
            const alreadyLoaded: boolean = !!(courseGroupDetailsMap && courseGroupDetailsMap[courseGroupId])
            const alreadyFetching: boolean = !!(fetchingCourseGroupDetailsMap && fetchingCourseGroupDetailsMap[courseGroupId])

            if (alreadyLoaded || alreadyFetching) {
                return
            }

            // Use the set L1 from metadata, fallback to first supported L1
            let l1Id: number | null = metadata.l1Id
            if (!l1Id && metadata.supportedL1Ids && metadata.supportedL1Ids.length > 0) {
                l1Id = metadata.supportedL1Ids[0]
            }

            if (l1Id) {
                dispatch(studentCourseActions.getCourseGroupById(courseGroupId, l1Id))

                // Also fetch progression for this course group at the same time
                const progressionAlreadyLoaded = !!(courseGroupProgressionMap && courseGroupProgressionMap[courseGroupId])
                const progressionAlreadyFetching = !!(fetchingCourseGroupProgressionMap && fetchingCourseGroupProgressionMap[courseGroupId])
                if (!progressionAlreadyLoaded && !progressionAlreadyFetching) {
                    dispatch(studentCourseActions.getCourseGroupProgression(courseGroupId, l1Id))
                }
            }
        })
    }, [courseGroupOverview])

    // ─── Derive the ordered list of course groups to display ──────
    const orderedCourseGroups: OrderedCourseGroupEntry[] = useMemo(() => {
        if (!courseGroupOverview?.courseGroupMetadataV2Dtos || !Array.isArray(courseGroupOverview.courseGroupMetadataV2Dtos)) {
            return []
        }
        return [...courseGroupOverview.courseGroupMetadataV2Dtos]
            .filter((metadata: CourseGroupMetadataV2Dto) => metadata && metadata.courseGroupId)
            .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
            .map((metadata: CourseGroupMetadataV2Dto): OrderedCourseGroupEntry => {
                const details: CourseGroupV1Dto | null =
                    (courseGroupDetailsMap && courseGroupDetailsMap[metadata.courseGroupId]) || null
                const progression: CourseGroupProgressionV1Dto | null =
                    (courseGroupProgressionMap && courseGroupProgressionMap[metadata.courseGroupId]) || null
                const normalizedProgression: number | null =
                    progression ? progression.courseGroupNormalizedProgression : null
                return { metadata, details, normalizedProgression }
            })
    }, [courseGroupOverview, courseGroupDetailsMap, courseGroupProgressionMap])

    // ─── Selected course group state (from Redux) ─────────────────
    const selectedCourseGroupId = studentCourse.selectedCourseGroupId

    const handleCourseGroupSelect = useCallback((courseGroupId: string): void => {
        savedScrollRef.current = window.scrollY
        dispatch(studentCourseActions.setSelectedCourseGroupId(courseGroupId))
    }, [dispatch])

    const handleCloseDetail = useCallback((): void => {
        dispatch(studentCourseActions.setSelectedCourseGroupId(null))
        dispatch(studentCourseActions.setSelectedTopicId(null))
    }, [dispatch])

    useEffect(() => {
        if (!selectedCourseGroupId && savedScrollRef.current > 0) {
            requestAnimationFrame(() => {
                window.scrollTo(0, savedScrollRef.current)
            })
        }
    }, [selectedCourseGroupId])

    const selectedCourseGroup: CourseGroupV1Dto | null =
        selectedCourseGroupId && courseGroupDetailsMap
            ? courseGroupDetailsMap[selectedCourseGroupId] || null
            : null

    const selectedProgression: CourseGroupProgressionV1Dto | null =
        selectedCourseGroupId && courseGroupProgressionMap
            ? courseGroupProgressionMap[selectedCourseGroupId] || null
            : null

    const selectedMetadata: CourseGroupMetadataV2Dto | null = useMemo(() => {
        if (!selectedCourseGroupId || !courseGroupOverview?.courseGroupMetadataV2Dtos) return null
        return courseGroupOverview.courseGroupMetadataV2Dtos.find(
            (m: CourseGroupMetadataV2Dto) => m.courseGroupId === selectedCourseGroupId
        ) || null
    }, [selectedCourseGroupId, courseGroupOverview])

    // ─── Loading state ────────────────────────────────────────────
    if (fetchingCourseGroupOverview && !courseGroupOverview) {
        return (
            <div className={classes.root}>
                <div className={classes.loadingContainer}>
                    <CircularProgress size={40} style={{ color: CapeeshColors.Primary600 }} />
                    <Typography className={classes.loadingText}>
                        {intl.formatMessage(studentScreenMessages.loadingCourses)}
                    </Typography>
                </div>
            </div>
        )
    }

    // ─── Empty state ──────────────────────────────────────────────
    if (
        courseGroupOverview &&
        (!courseGroupOverview.courseGroupIds || courseGroupOverview.courseGroupIds.length === 0)
    ) {
        return (
            <div className={classes.root}>
                <div className={classes.emptyContainer}>
                    <Typography className={classes.emptyTitle}>
                        {intl.formatMessage(studentScreenMessages.noCoursesYet)}
                    </Typography>
                    <Typography className={classes.emptySubtitle}>
                        {intl.formatMessage(studentScreenMessages.noCoursesDescription)}
                    </Typography>
                </div>
            </div>
        )
    }

    // ─── Course group cards ───────────────────────────────────────
    return (
        <div className={classes.root} ref={rootRef}>
            <div className={classes.header}>
                <Typography className={classes.title}>
                    {intl.formatMessage(studentScreenMessages.myCourses)}
                </Typography>
                <Typography className={classes.subtitle}>
                    {intl.formatMessage(studentScreenMessages.coursesAvailable, { count: courseGroupOverview?.courseGroupIds?.length || 0 })}
                </Typography>
            </div>

            <div className={classes.cardGrid}>
                {orderedCourseGroups.map((entry: OrderedCourseGroupEntry, index: number) => {
                    if (!entry?.metadata?.courseGroupId) {
                        return null
                    }
                    const { metadata, details } = entry
                    const isLoadingDetails: boolean = !!(fetchingCourseGroupDetailsMap && fetchingCourseGroupDetailsMap[metadata.courseGroupId])

                    if (!details && isLoadingDetails) {
                        return (
                            <div
                                key={metadata.courseGroupId}
                                className={classes.placeholderCard}
                            >
                                <CircularProgress size={28} style={{ color: CapeeshColors.Gray2 }} />
                            </div>
                        )
                    }

                    if (!details) {
                        return null
                    }

                    return (
                        <StudentCourseGroupCard
                            key={metadata.courseGroupId}
                            courseGroup={details}
                            normalizedProgression={entry.normalizedProgression}
                            onSelect={handleCourseGroupSelect}
                            index={index}
                        />
                    )
                })}
            </div>

            {/* Course detail slide-in panel */}
            <CourseDetailPanel
                open={selectedCourseGroupId != null}
                courseGroup={selectedCourseGroup}
                progression={selectedProgression}
                metadata={selectedMetadata}
                settingL1={studentCourse.settingL1}
                courseLessonOrder={courseLessonOrder || null}
                courseLessonOrderProgression={courseLessonOrderProgression || null}
                fetchingCourseLessonOrder={fetchingCourseLessonOrder || false}
                fetchingCourseLessonOrderProgression={fetchingCourseLessonOrderProgression || false}
                restoredSelectedTopicId={studentCourse.selectedTopicId}
                dispatch={dispatch}
                onClose={handleCloseDetail}
            />
        </div>
    )
}

const mapStateToProps = (state: any): MainMenuStateProps => {
    const { studentCourse } = state
    return {
        studentCourse,
    }
}

export default connect(mapStateToProps)(MainMenu)
